import React, { useEffect, useRef, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Repeat, Plus, Save, Trash2, X, Upload, Car, ChevronUp, ChevronDown } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const fmt = (n) => Math.round(Number(n || 0)).toLocaleString("it-IT");
const API_BASE = process.env.REACT_APP_BACKEND_URL;
export const swapImageUrl = (path) => (path ? `${API_BASE}/api/files/${path}` : null);

const EMPTY = { base_name: "", target_name: "", dealer_price: "", workshop_price: "", image_path: null };

export default function GestioneSwap() {
  const { user } = useAuth();
  const isOwner = user?.role === "Proprietario";
  const [swaps, setSwaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/swaps");
      setSwaps(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore caricamento swap");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const pickImage = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_path: data.path }));
      toast.success("Foto caricata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Caricamento foto non riuscito"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    const dealer = parseFloat(form.dealer_price);
    const workshop = parseFloat(form.workshop_price);
    if (!form.target_name.trim()) { toast.error("Inserisci il veicolo finale (swap)"); return; }
    if (Number.isNaN(dealer) || Number.isNaN(workshop)) { toast.error("Inserisci prezzi validi"); return; }
    setBusy(true);
    const payload = {
      base_name: form.base_name.trim(),
      target_name: form.target_name.trim(),
      dealer_price: dealer,
      workshop_price: workshop,
      image_path: form.image_path,
    };
    try {
      if (editingId) await api.patch(`/swaps/${editingId}`, payload);
      else await api.post("/swaps", payload);
      toast.success(editingId ? "Swap aggiornato" : "Swap creato");
      setForm(EMPTY);
      setEditingId(null);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const edit = (s) => {
    setEditingId(s.id);
    setForm({
      base_name: s.base_name || "",
      target_name: s.target_name || "",
      dealer_price: String(s.dealer_price ?? ""),
      workshop_price: String(s.workshop_price ?? ""),
      image_path: s.image_path || null,
    });
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= swaps.length) return;
    const next = [...swaps];
    [next[index], next[target]] = [next[target], next[index]];
    setSwaps(next);
    try {
      await api.post("/swaps/reorder", { ids: next.map((s) => s.id) });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Eliminare lo swap "${s.target_name}"?`)) return;
    try {
      await api.delete(`/swaps/${s.id}`);
      toast.success("Swap eliminato");
      if (editingId === s.id) { setEditingId(null); setForm(EMPTY); }
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!isOwner) {
    return (
      <div className="w-full">
        <PageHeader icon={Repeat} title="Gestione Swap" subtitle="Configurazione swap." />
        <div className="ot-panel p-6 text-slate-400 text-sm" data-testid="gestione-swap-no-access">
          Solo il Proprietario può gestire gli swap.
        </div>
      </div>
    );
  }

  const totalPreview = (parseFloat(form.dealer_price) || 0) + (parseFloat(form.workshop_price) || 0);

  return (
    <div className="w-full">
      <PageHeader
        icon={Repeat}
        title="Gestione Swap"
        subtitle="Aggiungi foto, prezzo concessionario e prezzo officina di ogni swap."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 ot-panel-elev p-4 ot-fade-up">
          <h2 className="ot-heading text-lg font-bold text-slate-100 mb-3">Swap Configurati</h2>
          {loading ? (
            <div className="text-slate-500 text-sm">Caricamento...</div>
          ) : swaps.length === 0 ? (
            <div className="text-slate-500 text-sm">Nessuno swap configurato. Creane uno dal pannello a destra.</div>
          ) : (
            <div className="space-y-2" data-testid="swaps-admin-list">
              {swaps.map((s, idx) => (
                <div key={s.id} className="ot-panel p-3 flex items-center gap-2 sm:gap-3 flex-wrap" data-testid={`swap-admin-row-${s.id}`}>
                  <div className="flex flex-col shrink-0">
                    <button onClick={() => move(idx, -1)} data-testid={`swap-up-${s.id}`} className="p-1 rounded text-slate-400 hover:text-blue-400 hover:bg-white/5 transition-colors">
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button onClick={() => move(idx, 1)} data-testid={`swap-down-${s.id}`} className="p-1 rounded text-slate-400 hover:text-blue-400 hover:bg-white/5 transition-colors">
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                  {s.image_path ? (
                    <img src={swapImageUrl(s.image_path)} alt={s.target_name} className="w-20 h-14 object-cover rounded border border-blue-400/20" />
                  ) : (
                    <div className="w-20 h-14 rounded border border-blue-400/20 flex items-center justify-center">
                      <Car className="w-5 h-5 text-slate-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-[55%] sm:min-w-0">
                    <div className="ot-heading text-sm font-bold text-slate-100 uppercase">{s.target_name}</div>
                    <div className="ot-mono text-[0.65rem] text-slate-500 truncate">BASE: {s.base_name || "—"}</div>
                  </div>
                  <div className="text-right hidden sm:block">
                    <div className="ot-mono text-xs text-slate-400">Conc. ${fmt(s.dealer_price)}</div>
                    <div className="ot-mono text-xs text-blue-400">Off. ${fmt(s.workshop_price)}</div>
                    <div className="ot-mono text-xs font-black text-amber-400">Tot. ${fmt(s.total)}</div>
                  </div>
                  <button onClick={() => edit(s)} data-testid={`edit-swap-${s.id}`} className="px-2 py-1 rounded text-[0.65rem] ot-heading tracking-widest text-blue-400 border border-blue-400/30 hover:bg-blue-400/10 transition-colors">
                    MODIFICA
                  </button>
                  <button onClick={() => remove(s)} data-testid={`delete-swap-${s.id}`} className="p-1.5 rounded text-red-400 hover:bg-red-500/10 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="ot-panel-elev p-4 ot-fade-up" style={{ animationDelay: "80ms" }}>
          <h2 className="ot-heading text-lg font-bold text-slate-100 mb-3">{editingId ? "Modifica Swap" : "Nuovo Swap"}</h2>
          <div className="space-y-3">
            <div>
              <label className="ot-label block mb-1">FOTO</label>
              {form.image_path && (
                <img src={swapImageUrl(form.image_path)} alt="anteprima" className="w-full h-32 object-cover rounded border border-blue-400/20 mb-2" data-testid="swap-image-preview" />
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => pickImage(e.target.files?.[0])} data-testid="swap-image-input" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="ot-btn-ghost w-full flex items-center justify-center gap-2" data-testid="swap-image-btn">
                <Upload className="w-3.5 h-3.5" />
                {uploading ? "Caricamento..." : form.image_path ? "Cambia foto" : "Carica foto dal PC"}
              </button>
            </div>
            <div>
              <label className="ot-label block mb-1">VEICOLO BASE (CONCESSIONARIO)</label>
              <input data-testid="swap-base-name" className="ot-input" placeholder="Es. Karin Futo" value={form.base_name} onChange={(e) => setForm({ ...form, base_name: e.target.value })} />
            </div>
            <div>
              <label className="ot-label block mb-1">VEICOLO FINALE (SWAP)</label>
              <input data-testid="swap-target-name" className="ot-input" placeholder="Es. Nissan Silvia S15" value={form.target_name} onChange={(e) => setForm({ ...form, target_name: e.target.value })} />
            </div>
            <div>
              <label className="ot-label block mb-1">PREZZO CONCESSIONARIO ($)</label>
              <input data-testid="swap-dealer-price" type="number" className="ot-input ot-mono" placeholder="0" value={form.dealer_price} onChange={(e) => setForm({ ...form, dealer_price: e.target.value })} />
            </div>
            <div>
              <label className="ot-label block mb-1">PREZZO OFFICINA ($)</label>
              <input data-testid="swap-workshop-price" type="number" className="ot-input ot-mono" placeholder="0" value={form.workshop_price} onChange={(e) => setForm({ ...form, workshop_price: e.target.value })} />
            </div>
            <div className="ot-panel px-3 py-2 flex items-center justify-between">
              <span className="ot-label">TOTALE CLIENTE</span>
              <span className="ot-mono font-black text-amber-400" data-testid="swap-total-preview">${fmt(totalPreview)}</span>
            </div>
            <button data-testid="save-swap-btn" disabled={busy} onClick={save} className="ot-btn-primary w-full flex items-center justify-center gap-2">
              {editingId ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              {editingId ? "Salva Modifiche" : "Crea Swap"}
            </button>
            {editingId && (
              <button data-testid="cancel-swap-btn" onClick={() => { setEditingId(null); setForm(EMPTY); }} className="ot-btn-ghost w-full flex items-center justify-center gap-2">
                <X className="w-4 h-4" /> Annulla
              </button>
            )}
            <p className="text-slate-500 text-xs leading-relaxed">
              In fattura viene conteggiato solo il <b>prezzo officina</b>; il concessionario lo paga il cliente a parte.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
